import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ArrowLeft, ArrowRight, CheckCircle2, Loader2 } from 'lucide-react';
import api from '@/lib/api';
import { FormField } from '@/components/form-builder/FormBuilder';
import FileUpload from '@/components/ui/FileUpload';
import { TSystemsMark, TOrb } from '@/components/brand/TSystemsMark';

interface FormTemplate {
  id: string;
  name: string;
  formName: string;
  formTitle: string;
  formDescription: string;
  fields: FormField[];
  submitButtonText: string;
  successMessage: string;
  redirectUrl: string;
  theme: {
    primaryColor: string;
    secondaryColor: string;
    fontFamily: string;
    customCSS: string;
  };
  config: {
    allowMultipleSubmissions: boolean;
    showProgressBar: boolean;
    enableCaptcha: boolean;
    shuffleQuestions?: boolean;
    collectEmailMode?: 'none' | 'manual';
  };
}

const PublicFormView: React.FC = () => {
  const { formName } = useParams<{ formName: string }>();
  const { t } = useTranslation('common');
  const [formTemplate, setFormTemplate] = useState<FormTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [closingTab, setClosingTab] = useState(false);
  const [closeBlocked, setCloseBlocked] = useState(false);
  // Shuffled field order: computed once on load
  const [shuffledFieldIds, setShuffledFieldIds] = useState<string[] | null>(null);

  useEffect(() => {
    loadForm();
  }, [formName]);

  const closeCurrentTab = () => {
    try {
      window.close();
      window.open('', '_self');
      window.close();
    } catch (error) {
      console.warn('Could not close current tab automatically:', error);
    }
  };

  useEffect(() => {
    if (!submitted || !formTemplate || formTemplate.redirectUrl) return;

    setClosingTab(true);
    setCloseBlocked(false);

    const closeTimer = window.setTimeout(() => {
      closeCurrentTab();

      // If the tab is still open, browser likely blocked programmatic close.
      window.setTimeout(() => {
        if (!document.hidden) {
          setClosingTab(false);
          setCloseBlocked(true);
        }
      }, 500);
    }, 900);

    return () => window.clearTimeout(closeTimer);
  }, [submitted, formTemplate]);

  const loadForm = async () => {
    try {
      setLoading(true);
      setError(null);

      // Buscar form template por formName (não por ID)
      const response = await api.get(`/flow-engine/form-templates/by-name/${formName}`);

      if (response.data.success) {
        const template = response.data.formTemplate;

        // Inject required email field at the top if collectEmailMode = 'manual' and no email field exists
        if (template.config?.collectEmailMode === 'manual') {
          const hasEmailField = template.fields.some((f: FormField) => f.fieldType === 'email');
          if (!hasEmailField) {
            const emailField: FormField = {
              id: '_collect_email',
              fieldId: '_collect_email',
              fieldLabel: t('publicForm.emailField.label'),
              fieldType: 'email',
              fieldPlaceholder: t('publicForm.emailField.placeholder'),
              required: true,
            };
            template.fields = [emailField, ...template.fields];
          }
        }

        setFormTemplate(template);

        // Shuffle field order once on load
        if (template.config?.shuffleQuestions) {
          const ids = [...template.fields.map((f: FormField) => f.fieldId)];
          for (let i = ids.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [ids[i], ids[j]] = [ids[j], ids[i]];
          }
          setShuffledFieldIds(ids);
        }

        // Inicializar form data com valores padrão
        const initialData: Record<string, any> = {};
        template.fields.forEach((field: FormField) => {
          if (field.defaultValue) {
            initialData[field.fieldId] = field.defaultValue;
          }
        });
        setFormData(initialData);
      } else {
        setError(t('publicForm.notFoundTitle'));
      }
    } catch (err: any) {
      console.error('Error loading form:', err);
      setError(err.response?.data?.message || t('publicForm.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (fieldId: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [fieldId]: value,
    }));
  };

  /**
   * Evaluate conditional field visibility
   */
  const evaluateCondition = (field: FormField): boolean => {
    // If no conditional rules, always show
    if (!field.conditional) return true;

    const { field: dependentFieldId, operator, value: expectedValue } = field.conditional;
    const actualValue = formData[dependentFieldId];

    // If dependent field has no value yet, hide this field
    if (actualValue === undefined || actualValue === null || actualValue === '') {
      return false;
    }

    switch (operator) {
      case 'equals':
        return actualValue == expectedValue; // Loose equality for type coercion

      case 'not_equals':
        return actualValue != expectedValue;

      case 'contains':
        if (typeof actualValue === 'string') {
          return actualValue.toLowerCase().includes(String(expectedValue).toLowerCase());
        }
        if (Array.isArray(actualValue)) {
          return actualValue.includes(expectedValue);
        }
        return false;

      case 'greater_than':
        return Number(actualValue) > Number(expectedValue);

      case 'less_than':
        return Number(actualValue) < Number(expectedValue);

      default:
        return true;
    }
  };

  /**
   * Get total number of steps
   */
  const getTotalSteps = (): number => {
    if (!formTemplate) return 1;
    const steps = formTemplate.fields.map(f => f.step || 1);
    return Math.max(...steps, 1);
  };

  /**
   * Get fields for current step
   */
  const getFieldsForStep = (step: number): FormField[] => {
    if (!formTemplate) return [];
    return formTemplate.fields.filter(f => (f.step || 1) === step);
  };

  /**
   * Validate current step before allowing navigation
   */
  const validateCurrentStep = (): boolean => {
    const stepFields = getFieldsForStep(currentStep).filter(field => evaluateCondition(field));

    for (const field of stepFields) {
      if (field.required && !formData[field.fieldId]) {
        toast.error(t('publicForm.requiredField', { field: field.fieldLabel }));
        return false;
      }
    }

    return true;
  };

  /**
   * Navigate to next step
   */
  const handleNextStep = () => {
    if (validateCurrentStep()) {
      setCurrentStep(prev => Math.min(prev + 1, getTotalSteps()));
    }
  };

  /**
   * Navigate to previous step
   */
  const handlePreviousStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formTemplate) return;

    // Validação básica
    for (const field of formTemplate.fields) {
      if (field.required && !formData[field.fieldId]) {
        toast.error(t('publicForm.requiredField', { field: field.fieldLabel }));
        return;
      }
    }

    try {
      setSubmitting(true);

      const response = await api.post('/flow-engine/form-submissions', {
        formTemplateId: formTemplate.id,
        formName: formTemplate.formName,
        data: formData,
      });

      if (response.data.success) {
        setSubmitted(true);

        // Redirecionar se configurado
        if (formTemplate.redirectUrl) {
          setTimeout(() => {
            window.location.href = formTemplate.redirectUrl;
          }, 2000);
        }
      }
    } catch (err: any) {
      console.error('Error submitting form:', err);
      if (err.response?.status === 409) {
        toast.error(t('publicForm.alreadySubmitted'), { duration: 6000 });
      } else {
        toast.error(err.response?.data?.message || t('publicForm.submitError'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field: FormField) => {
    const value = formData[field.fieldId] || '';

    const commonClasses =
      'w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-slate-900 shadow-sm transition placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-4 focus:ring-brand-500/10';

    switch (field.fieldType) {
      case 'textarea':
        return (
          <textarea
            id={field.fieldId}
            value={value}
            onChange={(e) => handleChange(field.fieldId, e.target.value)}
            placeholder={field.fieldPlaceholder}
            required={field.required}
            minLength={field.validation?.min}
            maxLength={field.validation?.max}
            rows={4}
            className={commonClasses}
            title={field.validation?.message}
          />
        );

      case 'select':
        return (
          <select
            id={field.fieldId}
            value={value}
            onChange={(e) => handleChange(field.fieldId, e.target.value)}
            required={field.required}
            className={commonClasses}
          >
            <option value="">{t('formBuilder.canvas.selectOption')}</option>
            {field.options?.map((option, index) => (
              <option key={index} value={option}>
                {option}
              </option>
            ))}
          </select>
        );

      case 'checkbox':
        return (
          <div className="space-y-2">
            {field.options?.map((option, index) => (
              <label
                key={index}
                className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 transition hover:border-slate-300 hover:bg-slate-100"
              >
                <input
                  type="checkbox"
                  value={option}
                  checked={(value as string[])?.includes(option) || false}
                  onChange={(e) => {
                    const currentValues = (value as string[]) || [];
                    const newValues = e.target.checked
                      ? [...currentValues, option]
                      : currentValues.filter(v => v !== option);
                    handleChange(field.fieldId, newValues);
                  }}
                  className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                />
                <span className="text-sm text-slate-700">{option}</span>
              </label>
            ))}
          </div>
        );

      case 'radio':
        return (
          <div className="space-y-2">
            {field.options?.map((option, index) => (
              <label
                key={index}
                className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 transition hover:border-slate-300 hover:bg-slate-100"
              >
                <input
                  type="radio"
                  name={field.fieldId}
                  value={option}
                  checked={value === option}
                  onChange={(e) => handleChange(field.fieldId, e.target.value)}
                  required={field.required}
                  className="mt-0.5 h-4 w-4 border-slate-300 text-brand-600 focus:ring-brand-500"
                />
                <span className="text-sm text-slate-700">{option}</span>
              </label>
            ))}
          </div>
        );

      case 'file':
        return (
          <FileUpload
            formName={formTemplate!.formName}
            fieldId={field.fieldId}
            accept={field.validation?.pattern || 'image/*,.pdf,.doc,.docx,.xls,.xlsx'}
            maxSizeMB={field.validation?.max || 10}
            multiple={false}
            required={field.required}
            onChange={(fileUrls: File[] | File | null) => {
              // Store the file URL(s) in form data
              const first = Array.isArray(fileUrls) ? fileUrls[0] : fileUrls;
              handleChange(field.fieldId, first || '');
            }}
          />
        );

      default:
        // Prepare validation attributes based on field type
        const inputProps: any = {
          type: field.fieldType,
          id: field.fieldId,
          value: value,
          onChange: (e: any) => handleChange(field.fieldId, e.target.value),
          placeholder: field.fieldPlaceholder,
          required: field.required,
          className: commonClasses,
        };

        // Apply validations based on field type
        if (field.fieldType === 'text' || field.fieldType === 'email') {
          if (field.validation?.min) inputProps.minLength = field.validation.min;
          if (field.validation?.max) inputProps.maxLength = field.validation.max;
          if (field.validation?.pattern) inputProps.pattern = field.validation.pattern;
        }

        if (field.fieldType === 'number') {
          if (field.validation?.min !== undefined) inputProps.min = field.validation.min;
          if (field.validation?.max !== undefined) inputProps.max = field.validation.max;
        }

        if (field.fieldType === 'date') {
          if (field.validation?.min) inputProps.min = field.validation.min;
          if (field.validation?.max) inputProps.max = field.validation.max;
        }

        // Custom validation message (shown in browser tooltip)
        if (field.validation?.message) {
          inputProps.title = field.validation.message;
        }

        return <input {...inputProps} />;
    }
  };

  const primaryColor = formTemplate?.theme?.primaryColor || '#e20074';
  const secondaryColor = formTemplate?.theme?.secondaryColor || '#7f123f';
  const headerGradient = `linear-gradient(90deg, ${primaryColor}, ${secondaryColor})`;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[radial-gradient(circle_at_top,#fdf0f7_0%,#fff7fb_45%,#fcf0f6_100%)] px-4">
        <div className="rounded-2xl border border-[#e9e5eb] bg-white px-8 py-10 text-center shadow-lg">
          <Loader2 className="mx-auto mb-4 h-10 w-10 animate-spin text-brand-600" />
          <p className="text-sm text-slate-600">{t('formBuilder.loading')}</p>
        </div>
      </div>
    );
  }

  if (error || !formTemplate) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[radial-gradient(circle_at_top,#fdf0f7_0%,#fff7fb_45%,#fcf0f6_100%)] px-4">
        <div className="w-full max-w-md rounded-2xl border border-[#e9e5eb] bg-white p-8 text-center shadow-lg">
          <AlertTriangle className="mx-auto mb-4 h-12 w-12 text-brand-600" />
          <h1 className="mb-2 text-2xl font-bold text-slate-900">
            {t('publicForm.notFoundTitle')}
          </h1>
          <p className="text-slate-600">
            {error || t('publicForm.notFoundDescription')}
          </p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-soft-brand px-4">
        <div className="w-full max-w-md rounded-[28px] border border-slate-200 bg-white p-8 text-center shadow-[0_30px_120px_-60px_rgba(40,36,46,0.45)]">
          <div className="mb-5 flex justify-center">
            <TOrb size={72} />
          </div>
          <h1 className="mb-3 text-2xl font-semibold text-slate-900">
            {formTemplate.successMessage || t('publicForm.successMessage')}
          </h1>
          {formTemplate.redirectUrl && (
            <p className="text-slate-600">{t('publicForm.redirecting')}</p>
          )}
          {/* "Send another response" link — shown only when allowMultipleSubmissions is true */}
          {formTemplate.config?.allowMultipleSubmissions && !formTemplate.redirectUrl && (
            <button
              type="button"
              onClick={() => {
                setSubmitted(false);
                setFormData({});
                setCurrentStep(1);
              }}
              className="mt-4 text-sm font-medium text-brand-600 hover:text-brand-700 hover:underline"
            >
              {t('publicForm.sendAnotherResponse')}
            </button>
          )}
          {!formTemplate.redirectUrl && closingTab && (
            <p className="mt-2 text-sm text-slate-600">
              {t('publicForm.closingTab')}
            </p>
          )}
          {!formTemplate.redirectUrl && closeBlocked && (
            <div className="mt-4 space-y-3">
              <p className="text-sm text-slate-600">
                {t('publicForm.closeBlocked')}
              </p>
              <button
                type="button"
                onClick={closeCurrentTab}
                className="inline-flex items-center justify-center rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                {t('publicForm.closeTabNow')}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Apply shuffled order if configured
  const orderedFields = (() => {
    if (!formTemplate) return [];
    if (!shuffledFieldIds) return formTemplate.fields ?? [];
    return [...(formTemplate.fields)].sort(
      (a, b) =>
        shuffledFieldIds.indexOf(a.fieldId) - shuffledFieldIds.indexOf(b.fieldId)
    );
  })();

  const getOrderedFieldsForStep = (step: number): FormField[] => {
    return orderedFields.filter(f => (f.step || 1) === step);
  };

  const visibleFields = getOrderedFieldsForStep(currentStep).filter((field) => evaluateCondition(field));
  const showProgress = formTemplate.config?.showProgressBar && (getTotalSteps() > 1 || formTemplate.fields.length > 0);


  return (
    <div
      className="flex min-h-screen flex-col bg-soft-brand px-4 py-8 sm:px-6 lg:px-8"
      style={{ fontFamily: formTemplate.theme.fontFamily }}
    >
      <style>{formTemplate.theme.customCSS}</style>

      <div className="mx-auto w-full max-w-[760px]">
        <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_30px_120px_-60px_rgba(40,36,46,0.45)]">
          {/* Header gradient bar */}
          <div className="h-4" style={{ background: headerGradient }} />

          {/* Title & description */}
          <div className="border-b border-slate-100 p-8">
            <div className="mb-7 flex items-center justify-between gap-4">
              <img
                src="/t-systems.svg"
                alt="T-SYSTEMS"
                className="h-6 w-auto shrink-0"
                draggable={false}
              />
              <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-brand-600">
                <TOrb size={18} />
                AIDA Forms
              </span>
            </div>
            <h1 className="break-words text-[clamp(1.25rem,3.6vw,1.875rem)] font-semibold leading-tight text-slate-950">{formTemplate.formTitle}</h1>
            <p className="mt-3 text-sm leading-7 text-slate-500">
              {formTemplate.formDescription || ''}
            </p>
          </div>

          {/* Form body */}
          <div className="p-5 sm:p-8">
            {/* Progress bar */}
            {showProgress && (() => {
              const isMultiStep = getTotalSteps() > 1;
              const pct = isMultiStep
                ? Math.round((currentStep / getTotalSteps()) * 100)
                : Math.round((Object.keys(formData).filter(k => formData[k] !== '' && formData[k] !== undefined && formData[k] !== null).length / Math.max(visibleFields.length, 1)) * 100);
              const label = isMultiStep
                ? t('publicForm.stepOf', { current: currentStep, total: getTotalSteps() })
                : t('formBuilder.preview.progress');
              return (
                <div className="mb-6">
                  <div className="mb-2 flex items-center justify-between text-[11px] font-medium uppercase tracking-[0.18em] text-slate-400">
                    <span>{label}</span>
                    <span>{pct}%</span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                    <div
                      className="h-full rounded-full transition-all duration-300"
                      style={{ width: `${pct}%`, backgroundColor: primaryColor }}
                    />
                  </div>
                </div>
              );
            })()}

            <form onSubmit={handleSubmit} className="space-y-5">
              {visibleFields.map((field) => (
                <section
                  key={field.fieldId}
                  className="rounded-[24px] border border-slate-200 bg-slate-50 p-5"
                >
                  <label
                    htmlFor={field.fieldId}
                    className="mb-3 block text-sm font-medium text-slate-900"
                  >
                    {field.fieldLabel}
                    {field.required && <span className="ml-1 text-red-500">*</span>}
                  </label>
                  {renderField(field)}
                  {field.validation?.message && (
                    <p className="mt-2 text-xs text-slate-400">{field.validation.message}</p>
                  )}
                </section>
              ))}

              <div className="pt-4">
                {/* Multi-step navigation */}
                {getTotalSteps() > 1 && (
                  <div className="flex items-center justify-between">
                    {currentStep > 1 ? (
                      <button
                        type="button"
                        onClick={handlePreviousStep}
                        className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                      >
                        <ArrowLeft className="h-4 w-4" />
                        {t('publicForm.previous')}
                      </button>
                    ) : <div />}

                    {currentStep < getTotalSteps() && (
                      <button
                        type="button"
                        onClick={handleNextStep}
                        className="inline-flex items-center gap-2 rounded-2xl px-5 py-3 text-sm font-medium text-white transition hover:opacity-90"
                        style={{ background: primaryColor }}
                      >
                        {t('publicForm.next')}
                        <ArrowRight className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                )}

                {/* Submit button — shown on last step or single-step form */}
                {(currentStep === getTotalSteps() || getTotalSteps() === 1) && (
                  <button
                    type="submit"
                    disabled={submitting}
                    style={{ background: primaryColor }}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-2xl px-6 py-3 text-sm font-medium text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                    {submitting ? t('publicForm.submitting') : (formTemplate.submitButtonText || t('formBuilder.defaults.submitButton'))}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicFormView;
